import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Icon from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    getSavedOrdersParameter: state.pages.savedOrderList.getCartsApiParameter,
    language: state.context.session.language,
    savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    orderDateHeader?: DataTableHeaderPresentationProps;
    shipToLabelHeader?: DataTableHeaderPresentationProps;
    subtotalHeader?: DataTableHeaderPresentationProps;
    orderDateCell?: DataTableCellBaseProps;
    shipToLabelCell?: DataTableCellBaseProps;
    subtotalCell?: DataTableCellBaseProps;
    orderView?: InjectableCss;
    orderViewHeader?: InjectableCss;
    mobileHeading?: InjectableCss;
}

export const savedOrderListTableStyles: SavedOrderListTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-top: -10px;

            thead {
                border-radius: 0 0 2px 2px;
                background-color: #E5EEF7;

                th {
                    background-color: #E5EEF7;
                    height: 40px;
                    border-bottom: none;
                }
            }

            tr {
                box-shadow: 0 2px 12px 0 rgba(0,37,84,0.04);
                &:nth-child(odd),
                &:nth-child(even) {
                    background-color: #fff !important;
                }
            }

            tbody {
                td {
                    &:last-child {
                        border-bottom: none;
                    }
                    span {
                        color: #2E384D;
                        font-family: Lato;
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 22px;
                    }
                }
            }

            @media screen and (min-width: 768px) {
                tbody {
                    td {
                        border-bottom: none;

                        &:first-child {
                            width: 30px;
                            max-width: 30px;
                        }
                    }

                    td[data-test-selector="savedOrderTable_tableCell_date"] {
                        width: 40px;
                        max-width: 40px;
                    }
                }

                thead {
                    th {
                        &:first-child {
                            width: 30px;
                            max-width: 30px;
                        } 
                    }
                    
                    th[data-test-selector="savedOrderTable_tableHead_date"] {
                        width: 40px;
                        max-width: 40px;
                    }
            }

            @media screen and (min-width: 1280px) {
                thead {
                    th {
                        &:first-child {
                            padding-left: 30px;
                            width: 80px;
                            max-width: 80px;
                        }
                        &:last-child {
                            padding-right: 30px;
                        }
                    }
                    th[data-test-selector="savedOrderTable_tableHead_date"] {
                        width: 125px;
                        max-width: 125px;
                    }
                }
                tbody {
                    td {
                        &:first-child {
                            padding-left: 30px;
                            width: 80px;
                            max-width: 80px;
                        }
                        &:last-child {
                            padding-right: 30px;
                        }
                    }
                    td[data-test-selector="savedOrderTable_tableCell_date"] {
                        width: 125px;
                        max-width: 125px;
                    }
                }
            }
        `,
    },
    orderDateHeader: {
        css: css`
            color: #2060AD;
            font-family: Roboto;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0.81px;
            line-height: 16px;
            justify-content: flex-start;

            button {
                justify-content: flex-start;
                span {
                    color: #2060AD;
                    font-family: Roboto;
                    font-size: 14px;
                    font-weight: bold;
                    letter-spacing: 0.81px;
                    line-height: 16px;
                    justify-content: flex-start;
                }
            }
        `,
    },
    shipToLabelHeader: {
        css: css`
            color: #2060AD;
            font-family: Roboto;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0.81px;
            line-height: 16px;
        `,
    },
    dataTableHead: {
        css: css`
            border-radius: 0 0 2px 2px;
            background-color: #E5EEF7;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 0;

            table {
                border-collapse: collapse;

                td:last-child {
                    border-bottom: none;
                }
            }
            th {
                background-color: #E5EEF7;
                height: 40px;
            }
            @media screen and (max-width: 767px) {
                table {
                    td {
                        padding: 11px 0 10px 10px;
                        > span {
                            max-width: 100%;
                            width: 100%;
                            color: #2E384D !important;
                            font-family: 'Lato',sans-serif;
                            font-size: 14px;
                            text-align: left !important;
                            justify-content: flex-start !important;
                        }
                    }
                }
            }
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    subtotalHeader: {
        css: css`
            color: #2060AD;
            font-family: Roboto;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0.81px;
            line-height: 16px;
            text-align: right;
        `,
        sortClickableProps: {
            css: css`
                justify-content: flex-end;
                width: 100%;
            `,
        },
    },
    subtotalCell: {
        alignX: "right",
        css: css `
            color: #2E384D;
            font-family: Lato;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 22px;
            text-align: right;

            span {

            }
        `,
    },
    orderView: {
        css: css`
            padding: 0 .938rem;
            position: relative;
            text-align: center;
            align-items: center;
            justify-content: center;

            @media screen and (min-width: 1280px) {
                text-align: -webkit-left;
                justify-content: flex-start;
                margin-left: 0 !important;
                padding-left: 0 !important;
                
            }

            &:after {
                content: '';
                display: block;
                height: 1.875rem;
                width: .063rem;
                position: absolute;
                right: -.938rem;
                border-right: .063rem solid #E0E0E0;
            }

            @media screen and (min-width: 1280px) {
                &:after {
                    right: auto;
                    left: 2.5rem;
                }
            }

            @media screen and (max-width: 767px) {
                &:after {
                    content: none;
                }
            }
        `,
    },
    dataTable: {
        css: css`
            border-spacing: 0 15px;
            background: #f7f9fa;
            border-collapse: separate;
            th {
                border-bottom: 0;
                color: #2060AD;
                font-family: 'Roboto', sans-serif;
                font-size: 14px;
                font-weight: bold;
                letter-spacing: .81px;
                line-height: 16px;

                span {
                    font-size: 14px;
                }

                &:first-child {
                    font-weight: normal;
                    letter-spacing: 0;

                    span {
                        font-weight: normal;
                        letter-spacing: 0;
                    }
                }
                
                button {
                    color: #2060AD;
                    font-family: 'Roboto', sans-serif;
                    font-size: 14px;
                    font-weight: bold;
                    letter-spacing: .81px;
                    line-height: 16px;
                    width: auto;  
                }

                svg {
                    color: #2E5BFF;
                }
            }
            td {
                color: #2E384D;
                font-family: 'Lato',sans-serif;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 22px;

                span, a {
                    color: #2E384D !important;
                    font-family: 'Lato',sans-serif;
                    font-size: 14px;
                }
            }

            @media screen and (min-width: 1280px) {
                thead {
                    position: relative;
                    top: 14px;
                    border-radius: 0 0 2px 2px;
                    
                }
                th {
                    height: 40px;
                    background-color: #E5EEF7;
                    width: auto;
                    text-align: left;

                    &:first-child {
                        padding-left: 30px;
                    }
                    &:last-child {
                        padding-right: 30px;
                        min-width: 5.625rem;
                        text-align: right;
                    }
                }
                td {
                    height: 60px;

                    &:first-child {
                        padding-left: 30px;
                    }
                    &:last-child {
                        padding-right: 30px;
                        min-width: 5.625rem;
                    }
                    span.shipping-value {
                        text-transform: capitalize;
                    }
                }

                tbody {
                    th {
                        display: none;
                    }
                }
            }

            @media screen and (min-width: 1300px) {
                th {
                    &:last-child {
                        min-width: 9.625rem;
                    }
                }
                td {
                    &:last-child {
                        min-width: 9.625rem;
                    }
                }
            }

            @media screen and (max-width: 767px) {
                &.table-with-items {
                    thead {
                        display: none;
                    }
                }

                &.table-with-items {
                    table,
                    tbody,
                    tr{
                        display: flex;
                        max-width: 100%;
                        flex-direction: column;
    
                        td[data-test-selector="savedOrderTable_tableCell_date"] {
                            display: none;
                        }
                    }
    
                    .mob-date-value {
                        position: absolute;
                        right: 40px;
                        color: #2E384D;
                        font-family: Lato;
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 22px;
                        text-align: right;
                        font-weight: normal;
                    }
    
                    th {
                        background: transparent !important;
                        height: auto !important;
                        border-bottom: 1px solid #D7D7D7 !important;
                        line-height: 24px !important;
                        padding: 4px 10px !important;
                        margin: 0 15px;
                        width: initial;
                    }
    
                    tr {
                        margin-bottom: 20px;
                        
                        &:before {
                            height: 27px;
                            width: 100%;
                            border-radius: 0 0 2px 2px;
                            background-color: #E5EEF7;
                            content: '';
                        }
                    }
    
                    td {
                        display: flex;
                        align-content: center;
                        height: auto;
                        border-color: #D7D7D7;
                        line-height: 24px;
                        padding: 4px 10px;
                        margin: 0 15px;
                        justify-content: space-between;
    
                        &.total-container {
                            padding: 27px 10px;
                            margin: 0 15px;
                            border: none;
    
                            span:last-child {
                                font-weight: bold;
                                font-size: 16px;
                                letter-spacing: 0.57px;
                            }
                        }
    
                        &:first-child {
                            font-weight: normal;
                            letter-spacing: 0;
                            padding: 11px 0 0 10px;
                            
                            span {
                                font-weight: normal;
                                letter-spacing: 0;
                            }
                        }
    
                        > span {
                            display: inline-flex;
                            align-items: center;
                            max-width: 49%;
                            width: 49%;
                            text-align: right;
                            white-space: normal;
                            justify-content: flex-end;
    
                            &:first-child {
                                justify-content: flex-start;
                                color: #2060AD !important;
                                font-family: 'Roboto', sans-serif;
                                letter-spacing: 0.81px;
                            }
    
                            &.shipping-value {
                                text-transform: capitalize;
                            }
    
                            &:last-child {
                                justify-content: flex-end;
                                color: #2E384D;
                                font-family: Lato;
                                font-size: 14px;
                                letter-spacing: 0;
                                line-height: 22px;
                                text-align: right;
                                font-weight: normal;
                            }
                        }
    
                        a {
                            text-align: right;
                            color: #2E384D !important;
                            font-family: Lato;
                            font-size: 14px;
                            letter-spacing: 0;
                            line-height: 22px;
                            text-align: right;
                            font-weight: normal;
    
                            span {
                                color: #2E384D !important;
                            }
    
                        }
                    }
                }

            }

            @media screen and (min-width: 768px) {
                tbody {
                    th {
                        display: none;
                    }
                }
            }
        `,
    },
    mobileHeading: {
        css: css`
            @media screen and (min-width: 768px) {
                display: none;
            }

            @media screen and (max-width: 767px) {
                background: none;
                display: inline-flex;
                width: 49%;
                color: #2060AD;
                font-family: 'Roboto', sans-serif;
                font-size: 14px;
                font-weight: bold;
                letter-spacing: .81px;
                line-height: 16px;
                align-items: center;
                text-align: left;
                padding: 0;
                height: auto;

                button {
                    span {
                        font-weight: bold !important;
                        font-size: 14px;
                    }
                }
            }
        `,
    },
    orderViewHeader: {
        css: css`
            color: #2060AD;
            font-family: Roboto;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 16px;
            font-wight: normal;
        `,
    }
};

const styles = savedOrderListTableStyles;

const SavedOrderListTable = ({
    getSavedOrdersParameter,
    updateSearchFields,
    language,
    savedOrderDetailsLink,
}: Props) => {
    const headerClick = (sortField: string) => {
        const sort = getSavedOrdersParameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (getSavedOrdersParameter.sort === sortField) {
            sorted = "ascending";
        } else if (getSavedOrdersParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const savedOrderListDataView = useContext(CartsDataViewContext);
    if (!savedOrderListDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (savedOrderListDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.dataTableHead}>
                        <DataTableHeader
                            tight
                        >   
                            <Typography {...styles.orderViewHeader}>
                                {translate("View", "View")}
                            </Typography>
                        </DataTableHeader>

                        <DataTableHeader
                            tight
                            sorted={sorted("orderDate")}
                            onSortClick={() => headerClick("orderDate")}
                            {...styles.orderDateHeader}
                        >
                            {translate("Cart Date")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("customerSequence")}
                            {...styles.shipToLabelHeader}
                        >
                            {translate("Name")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("orderSubtotal")}
                            {...styles.subtotalHeader}
                        >
                            {translate("Cart Subtotal")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody}>
                        <DataTableRow {...styles.dataTableRow}>   
                            <DataTableCell {...styles.orderDateCell} colSpan={4}>
                                <Typography {...styles.noResultsText}>{translate("No Saved Carts within the last 90 days.")}</Typography>
                            </DataTableCell>
                        </DataTableRow>
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        );
    }

    const rows = savedOrderListDataView.value.map(savedOrder => {
        return {
            id: savedOrder.id,
            date: savedOrder.orderDate
                ? getLocalizedDateTime({
                      dateTime: new Date(savedOrder.orderDate),
                      language,
                  })
                : "",
            shipToLabel:
                savedOrder.fulfillmentMethod === "PickUp" && savedOrder.defaultWarehouse
                    ? savedOrder.defaultWarehouse.description || savedOrder.defaultWarehouse.name
                    : savedOrder.collectionShipToLabel ?? savedOrder.shipToLabel,
            subtotal: savedOrder.orderSubTotalDisplay,
        };
    });

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable} className="table-with-items">
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader
                        tight
                        {...styles.orderViewHeader}
                    >   
                        {translate("View", "View")}
                    </DataTableHeader>

                    <DataTableHeader
                        tight
                        sorted={sorted("orderDate")}
                        onSortClick={() => headerClick("orderDate")}
                        data-test-selector="savedOrderTable_tableHead_date"
                        {...styles.orderDateHeader}
                    >
                        {translate("Cart Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("customerSequence")}
                        {...styles.shipToLabelHeader}
                    >
                        {translate("Name")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("orderSubtotal")}
                        {...styles.subtotalHeader}
                    >
                        {translate("Cart Subtotal")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(({ id, date, shipToLabel, subtotal }) => (
                        <DataTableRow
                            data-test-selector="savedOrderList_orderLine"
                            data-test-key={id}
                            key={id}
                            {...styles.dataTableRow}
                        >   
                            <DataTableCell {...styles.orderDateCell}>
                                <Typography {...styles.mobileHeading}>
                                    {translate("View", "View")}
                                </Typography>
                                <Link href={`${savedOrderDetailsLink?.url}?cartId=${id}`}  {...styles.orderView}>
                                    <Icon src="Eye" size={24} />
                                </Link>
                            </DataTableCell>
                            <DataTableHeader
                                tight
                                sorted={sorted("orderDate")}
                                onSortClick={() => headerClick("orderDate")}
                                {...styles.orderDateHeader}
                            >
                                {translate("Cart Date")}
                                <Typography className="mob-date-value">{date}</Typography>
                            </DataTableHeader>
                            <DataTableCell {...styles.orderDateCell}
                             data-test-selector="savedOrderTable_tableCell_date"
                            >
                                <Typography>{date}</Typography>
                            </DataTableCell>
                            <DataTableCell {...styles.shipToLabelCell}>
                                <Typography {...styles.mobileHeading}>
                                    {translate("Name", "Name")}
                                </Typography>
                                <Typography>{shipToLabel}</Typography>
                                </DataTableCell>
                            <DataTableCell {...styles.subtotalCell} className="total-container">
                                <Typography {...styles.mobileHeading} className="total-label">
                                    {translate("Cart Subtotal", "Cart Subtotal")}
                                </Typography>
                                <Typography>{subtotal}</Typography>
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListTable),
    definition: {
        group: "Saved Order List",
        displayName: "Search Results Table",
        allowedContexts: ["SavedOrderListPage"],
        isSystem: true,
    },
};


export default widgetModule;
